'use client';

import { IProductDetail } from '@/api/types/public';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import {
  getBlockFlags,
  getBlockFlagsPrev,
  getFeatureValueUsed,
  getGroupInfo,
  removeGroupInfo,
  setBlockFlags,
} from '@/utils/feature-value';
import { getProductDisplayLabel, getProductImages, getWishNumber } from '@/utils/goods-list-utils';

import { IProductDetailsPageInteractionBtnClickParams } from '../components/CustomerReviewsItem';

import {
  getSiteRedirectEnterPageScene,
  getSiteRedirectReferrerInfo,
} from '@/hooks/useSiteRedirect';
import { env } from '@/utils/env';
import { getReviewNum, getReviewRate } from '@/utils/goods-detail-utils';
import { genPrice } from '@/utils/price';
import { getRouteCnName, getRouteName } from '@/utils/route-metadata';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { getSession, randomString, removeSession } from '@/utils/tools';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useGoodsDetailTrack = ({
  product,
  reviewsData,
  isWeb,
}: {
  product: IProductDetail;
  reviewsData: any;
  isWeb: boolean;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { trackEvent } = useReporting();
  // const { isAttemptingNavigation, proceedNavigation } = useBlockNavigation(true);
  const enterTimer = useRef<number>(Date.now());
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { getFromRouterInfo } = useRouteInfo();
  const sd_image_type_list: ['', '商品图', '网红图', '素材图'] = ['', '商品图', '网红图', '素材图'];
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });
  // 保存进入页面的场景值，确保整个页面生命周期都能使用
  const enterPageSceneInternal = useRef<string>('');

  const product_view_uuid = useRef<string>('');

  useEffect(() => {
    if (product.shop_sku) {
      product_view_uuid.current = `product_${randomString()}`;
    }
  }, [product.shop_sku]);
  /**
   * @TrackType sensorsdata
   * @EventDesc - 试戴按钮曝光/点击
   */
  const SD_TryONBtn_Exp = useCallback((btn_name: string, item: any, sdEventName: string) => {
    try {
      const sdEventParams = {
        page_name: getRouteCnName(pathname),
        url_path: window.location.href,
        try_type: btn_name,
        product_id: item.id,
        product_name: item.name,
        product_sku: item.sku,
        shop_sku: item.shop_sku,
        business_sku: item.business_sku,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  }, []);

  const getPrice = (item: any, _currency: string) => {
    if (
      userInfo.is_member === 1 &&
      item[`price_info_v${userInfo.member_level}`] &&
      item[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      return genPrice(item[`price_info_v${userInfo.member_level}`], _currency);
    }
    if (item.price_info) {
      return genPrice(item.price_info, _currency);
    }
    return { price: 0, sell_price: 0 };
  };
  // 获取加购相关埋点的基础参数
  const getAddToCartBaseParams = useCallback(() => {
    return {
      product_view_uuid: product_view_uuid.current,
      entrance_edit_lens_scene: '',
      edit_lens_type: '',
      add_cart_scene: '商详页直接加购',
      product_class_name: product.products_category,
      is_product_lens: product.is_glass === 1 && product.product_lens === 1,
      shop_sku: product.shop_sku,
      business_sku: product.business_sku,
      product_sku: product.sku,
      product_id: `${product.id}`,
      product_name: product.short_name,
      campaign_type_id_list: product.buried_point_data?.campaign_type_id_list || [],
      refer_page_name: getRouteCnName(pathname) || getRouteName(pathname) || '',
      refer_url_path: window.location.href,
      is_glass: product.is_glass === 1,
      prescription_type_name: '',
      coating_id: '',
      coating_type_name: '',
      coating_price: '',
      current_coating_price: '',
      lens_id: '',
      lens_type_name: '',
      lens_color: '',
      lens_price: '',
      current_lens_price: '',
      lens_index_name: '',
      lens_index_price: '',
      current_lens_index_price: '',
      product_price: parseFloat(getPrice(product, currency.name).price),
      current_product_price: parseFloat(getPrice(product, currency.name).sell_price),
      base_current_product_price: parseFloat(
        getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
      ),
      currency: currency.name,
      dollar_exchange_rate: parseFloat(currency.exchange_rate),
      // 价值归因
      ...getFeatureValueUsed(product.shop_sku),
      // 呈现BI报表
      ...getBlockFlags(),
    };
  }, [product_view_uuid.current, product, currency, pathname]);

  const SDAddToCartBtnClick = useCallback(() => {
    try {
      const sdEventName = 'AddToCartBtn_Click';
      const sdEventParams = getAddToCartBaseParams();
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  }, [getAddToCartBaseParams]);

  const SDAddToCartResult = useCallback(
    (service_code: string, isSuccess: boolean, msg?: string) => {
      try {
        const sdEventName = 'AddToCart_Result';
        const sdEventParams = {
          ...getAddToCartBaseParams(),
          is_success: isSuccess,
          service_code,
          reorder_order_no: '',
          msg,
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [product_view_uuid.current],
  );

  // 获取商品详情页面埋点的基础参数
  const getProductDetailsBaseParams = useCallback(
    (isLeave = false) => {
      const { buried_point_data: { campaign_type_id_list } = {} } = product;
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const { group_id, group_name } = getGroupInfo();
      const params = {
        product_view_uuid: product_view_uuid.current,
        refer_page_name,
        refer_url_path,
        group_id,
        group_name,
        shop_sku: product.shop_sku,
        business_sku: product.business_sku,
        product_sku: product.sku,
        product_id: `${product.id}`,
        product_name: product.short_name,
        product_class_name: product.products_category,
        is_product_lens: product.is_glass === 1 && product.product_lens === 1,
        product_price: parseFloat(getPrice(product, currency.name).price),
        current_product_price: parseFloat(getPrice(product, currency.name).sell_price),
        base_current_product_price: parseFloat(
          getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
        ),
        currency: currency.name,
        campaign_type_id_list,
        product_display_label: getProductDisplayLabel(product as any, 2),
        product_collect_num: getWishNumber(product as any),
        product_review_num: getReviewNum(reviewsData || {}),
        product_rating: getReviewRate(reviewsData || {}),
        enter_page_scene:
          getSiteRedirectEnterPageScene() || enterPageSceneInternal.current || '其他',
        dollar_exchange_rate: parseFloat(currency.exchange_rate),
      };
      if (isLeave) {
        delete params.group_id;
        delete params.group_name;
        delete params.refer_page_name;
        delete params.refer_url_path;
      }
      return params;
    },
    [product, reviewsData, currency, product_view_uuid.current],
  );

  const SDProductDetailsPageView = useCallback(() => {
    try {
      const sdEventName = `ProductDetailsPage_View`;
      const referrerInfo = getSiteRedirectReferrerInfo();
      const baseParams = getProductDetailsBaseParams();
      const sdEventParams = {
        ...baseParams,
        ...getBlockFlags(),
        is_can_buy: product.qty > 0 && product.status === 1,
        from_site_info: referrerInfo.site_jump_from,
        refer_page_name: referrerInfo.referrer_page_name || baseParams.refer_page_name,
        refer_url_path: referrerInfo.referrer_url_path || baseParams.refer_url_path,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );

      // Emarsys view tracking
      trackEvent(
        {
          eventName: 'view',
          properties: product.shop_sku,
        },
        [ProviderType.EMARSYS],
      );
    } catch (e) {
      logger.info(e);
    }
  }, [product, getProductDetailsBaseParams]);

  const SDProductDetailsPageLeave = (_fromRouterInfo: any, prevBlogFlags: any, groupInfo: any) => {
    try {
      const sdEventName = 'ProductDetailsPage_Leave';
      const view_duration = Math.ceil((Date.now() - enterTimer.current) / 1000);
      const { viewAdvance, viewArea } = lastScrollState.current;
      const sdEventParams = {
        ...getProductDetailsBaseParams(true),
        refer_page_name: _fromRouterInfo.refer_page_name,
        refer_url_path: _fromRouterInfo.refer_url_path,
        view_duration,
        view_advance: viewAdvance,
        view_area: viewArea,
        ...prevBlogFlags,
        ...groupInfo,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };

  const SDProductDetailsPageBtnClick = useCallback(
    (btn_name: string) => {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      try {
        const sdEventName = 'ProductDetailsPageBtn_Click';
        const sdEventParams = {
          product_view_uuid: product_view_uuid.current,
          shop_sku: product.shop_sku,
          business_sku: product.business_sku,
          product_sku: product.sku,
          product_id: `${product.id}`,
          product_name: product.short_name,
          btn_name,
          product_class_name: product.products_category,
          is_product_lens: product.is_glass === 1 && product.product_lens === 1,
          refer_page_name,
          refer_url_path,
          campaign_type_id_list: product.buried_point_data?.campaign_type_id_list || [],
          product_display_label: getProductDisplayLabel(product as any, 2),
          product_price: parseFloat(getPrice(product, currency.name).price),
          current_product_price: parseFloat(getPrice(product, currency.name).sell_price),
          base_current_product_price: parseFloat(
            getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
          ),
          currency: currency.name,
          product_collect_num: getWishNumber(product as any),
          product_review_num: getReviewNum(reviewsData || {}),
          product_rating: getReviewRate(reviewsData || {}),
          dollar_exchange_rate: parseFloat(currency.exchange_rate),
          ...getBlockFlags(),
          ...getGroupInfo(),
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [product, currency, reviewsData],
  );

  const SDProductDetailsPageInteractionBtnClick = useCallback(
    ({
      page_name,
      interaction_type,
      interaction_name,
      comment_category,
      tab_name,
    }: IProductDetailsPageInteractionBtnClickParams) => {
      const { group_id, group_name } = getGroupInfo();
      try {
        const sdEventName = 'ProductDetailsPageInteractionBtn_Click';
        const sdEventParams = {
          product_view_uuid: product_view_uuid.current,
          page_name,
          url_path: window.location.href,
          group_id: group_id || '',
          group_name: group_name || '',
          shop_sku: product.shop_sku,
          business_sku: product.business_sku,
          product_sku: product.sku,
          product_id: `${product.id}`,
          product_name: product.short_name,
          interaction_type,
          interaction_name,
          product_class_name: product.products_category,
          is_product_lens: product.is_glass === 1 && product.product_lens === 1,
          comment_category,
          tab_name,
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [product],
  );

  const SDProductDetailsPageSeimilarMktClick = useCallback(
    (params) => {
      try {
        const sdEventName = 'ProductDetailsPageSeimilarMkt_Click';
        const sdEventParams = {
          ...params,
          product_view_uuid: product_view_uuid.current,
          main_shop_sku: product.shop_sku,
          main_business_sku: product.business_sku,
          main_product_sku: product.sku,
          main_product_id: `${product.id}`,
          main_product_name: product.short_name,
          main_product_price: parseFloat(getPrice(product, currency.name).price),
          main_product_current_price: parseFloat(getPrice(product, currency.name).sell_price),
          product_class_name: product.products_category,
          is_product_lens: product.is_glass === 1 && product.product_lens === 1,
          currency: currency.name,
          recall_group_type: params.recall_group_type,
          fine_ranking_type: params.fine_ranking_type,
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [product, currency],
  );
  const getRelatedMktSensorData = (item: any, index: number) => {
    return {
      product_view_uuid: product_view_uuid.current,
      product_index: index,
      main_shop_sku: product.shop_sku,
      main_business_sku: product.business_sku,
      main_product_sku: product.sku,
      main_product_id: `${product.id}`,
      main_product_name: product.short_name,
      shop_sku: item.shop_sku,
      business_sku: item.business_sku,
      product_sku: item.sku,
      product_id: `${item.id}`,
      product_name: item.short_name,
      product_class_name: product.products_category,
      is_product_lens: product.is_glass === 1 && product.product_lens === 1,
      // product_price: parseFloat(getPrice(item, currency.name).price),
      // current_product_price: parseFloat(getPrice(item, currency.name).sell_price),
      // base_current_product_price: parseFloat(getPrice(item, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price),
      product_display_label: getProductDisplayLabel(item, 2),
      product_collect_num: parseInt(item.wish_num, 10),
      product_review_num: parseInt(item.spu_review_count, 10),
      product_rating: parseFloat(item.star),
      related_picture_url: item.image,
      recall_group_type: item.recall_group_type,
      fine_ranking_type: item.fine_ranking_type,
      block_flags: 'goods_detail@related_0@',
      block_flags_ext: JSON.stringify({
        position_flag: '关联品推荐',
        shop_sku: item.shop_sku,
        block_shop_sku: product.shop_sku,
        block_product_id: `${product.id}`,
        // block_product_price: parseFloat(getPrice(item, currency.name).price),
        // block_cur_product_price: parseFloat(getPrice(item, currency.name).sell_price),
      }),
      main_product_price: parseFloat(getPrice(product, currency.name).price),
      main_product_current_price: parseFloat(getPrice(product, currency.name).sell_price),
      main_product_base_product_current_price: parseFloat(
        getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
      ),
      dollar_exchange_rate: parseFloat(currency.exchange_rate),
      currency: currency.name,
      first_picture_url: item.image,
    };
  };

  /**
   * 商品详情页关联品坑位曝光埋点
   */
  const SDProductDetailsPageRelatedMktExp = (params, index) => {
    try {
      const sdEventName = 'ProductDetailsPageRelatedMkt_Exp';
      const sdEventParams = getRelatedMktSensorData(params, index);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };
  /**
   * 商品详情页关联品坑位点击埋点
   */
  const SDProductDetailsPageRelatedMktClick = (params: any, index: number) => {
    try {
      const sdEventName = 'ProductDetailsPageRelatedMkt_Click';
      const sdEventParams = getRelatedMktSensorData(params, index);
      setBlockFlags({
        block_flags: sdEventParams.block_flags,
        block_flags_ext: sdEventParams.block_flags_ext,
        fine_ranking_type: sdEventParams.fine_ranking_type,
        recall_group_type: sdEventParams.recall_group_type,
        product_index: sdEventParams.product_index,
        first_picture_url: sdEventParams.first_picture_url,
      });
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };

  const SDProductDetailsPageProductPicExp = useCallback(
    (exp_advance) => {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const { group_id, group_name } = getGroupInfo();
      try {
        const sdEventName = 'ProductDetailsPageProductPic_Exp';
        const sdEventParams = {
          product_view_uuid: product_view_uuid.current,
          product_class_name: product.products_category,
          is_product_lens: product.is_glass === 1 && product.product_lens === 1,
          refer_page_name,
          refer_url_path,
          shop_sku: product.shop_sku,
          business_sku: product.business_sku,
          product_sku: product.sku,
          product_id: `${product.id}`,
          product_name: product.short_name,
          exp_advance,
          group_id,
          group_name,
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [product],
  );

  // 若有model_url，则展示 ar tryon按钮（优先级高），若有tryon_image_url，则展示 tryon按钮，
  const handleARTryOn = (item: any) => {
    let flag = '';
    if (item.glb_model) {
      flag = 'ar-tryon';
    } else if (item.tryon_image_url) {
      flag = 'tryon';
    }
    return flag;
  };
  const getYouAlsoLikeMktParams = useCallback(
    (params: any, listData: any[]) => {
      const index = listData.findIndex((item) => item.shop_sku === params.shop_sku);
      const mainShop = product;

      const { productImage, hoverImage } = getProductImages(params);

      return {
        product_index: index,
        sort_index: params.sort_index,
        business_sku: params.business_sku,
        shop_sku: params.shop_sku,
        product_sku: params.sku,
        product_id: `${params.id}`,
        product_name: params.short_name || params.name,
        product_price: Number(getPrice(params, currency.name).price),
        current_product_price: Number(getPrice(params, currency.name).sell_price),
        base_current_product_price: Number(
          getPrice(params, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
        ),
        currency: currency.name,
        product_display_label: getProductDisplayLabel(params, 4),
        product_collect_num: getWishNumber(params),
        product_rating: parseFloat(params.spu_review_stars || '0'),
        product_review_num: parseInt(params.spu_review_count as string, 10),
        first_picture_url: productImage.src,
        second_picture_url: isWeb ? hoverImage.src : '',
        recall_group_type: params.recall_group_type,
        fine_ranking_type: params.fine_ranking_type,
        display_color_num: params.correlation?.length || 0,
        is_display_ar_try_on: handleARTryOn(params) === 'ar-tryon',
        image_type: sd_image_type_list[productImage.type || 0] || '',
        hover_type:
          isWeb && sd_image_type_list[hoverImage.type || 0]
            ? sd_image_type_list[hoverImage.type || 0]
            : '',
        dollar_exchange_rate: parseFloat(currency.exchange_rate),
        product_view_uuid: product_view_uuid.current,
        product_class_name: mainShop.products_category,
        is_product_lens: mainShop.is_glass === 1 && mainShop.product_lens === 1,
        main_business_sku: mainShop.business_sku,
        main_shop_sku: mainShop.shop_sku,
        main_product_sku: mainShop.sku,
        main_product_id: `${mainShop.id}`,
        main_product_name: mainShop.short_name,
        main_product_price: parseFloat(getPrice(mainShop, currency.name).price),
        main_product_current_price: parseFloat(getPrice(mainShop, currency.name).sell_price),
        main_product_base_product_current_price: parseFloat(
          getPrice(mainShop, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
        ),
        recommend_position_type: 'productdetail_page_recommend',
        block_flags: 'goods_detail@recommend_0@',
        block_flags_ext: JSON.stringify({
          position_flag: '你也喜欢推荐',
          shop_sku: params.shop_sku,
          block_shop_sku: mainShop.shop_sku,
          block_product_id: `${mainShop.id}`,
          block_product_price: parseFloat(getPrice(mainShop, currency.name).price),
          block_cur_product_price: parseFloat(getPrice(mainShop, currency.name).sell_price),
        }),
      };
    },
    [
      product,
      currency,
      isWeb,
      sd_image_type_list,
      getProductDisplayLabel,
      getWishNumber,
      handleARTryOn,
    ],
  );

  const SDProductDetailsPageYouAlsoLikeMktExp = useCallback(
    (params: any, listData: any[]) => {
      try {
        const sdEventName = 'ProductDetailsPageYouAlsoLikeMkt_Exp';
        const sdEventParams = getYouAlsoLikeMktParams(params, listData);
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [getYouAlsoLikeMktParams],
  );
  const SDProductDetailsPageYouAlsoLikeMktValidExp = useCallback(
    (params: any, listData: any[]) => {
      try {
        const sdEventName = `ProductDetailsPageLikeValidMkt_Exp`;
        const sdEventParams = getYouAlsoLikeMktParams(params, listData);
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [getYouAlsoLikeMktParams],
  );

  const SDProductDetailsPageYouAlsoLikeMktClick = useCallback(
    (params: any, listData: any[]) => {
      try {
        const sdEventName = 'ProductDetailsPageYouAlsoLikeMkt_Click';
        const sdEventParams = getYouAlsoLikeMktParams(params, listData);
        setBlockFlags({
          block_flags: sdEventParams.block_flags,
          block_flags_ext: sdEventParams.block_flags_ext,
          fine_ranking_type: sdEventParams.fine_ranking_type,
          recall_group_type: sdEventParams.recall_group_type,
          product_index: sdEventParams.product_index,
          first_picture_url: sdEventParams.first_picture_url,
          refer_page_position: '你也喜欢推荐',
        });
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.info(e);
      }
    },
    [getYouAlsoLikeMktParams],
  );
  const token = useStore((state) => state.token);
  const visitorId = useStore((state) => state.visitorId);
  const [zlUserId, setZlUserId] = useState('');

  useEffect(() => {
    const localZlUserId = localStorage.getItem('zl-userId');
    if (localZlUserId) {
      setZlUserId(localZlUserId);
    }
  }, []);

  const ttqEventId = useMemo(() => {
    return token && product.shop_sku && zlUserId
      ? zlUserId + product.shop_sku
      : visitorId + product.shop_sku;
  }, [product.shop_sku, token, visitorId, zlUserId]);

  const sendThirdPartyAddToCart = useCallback(() => {
    try {
      const finalPrice = getPrice(product, currency.name).sell_price;

      trackEvent(
        undefined,
        [
          ProviderType.GA4,
          ProviderType.FACEBOOK,
          // ProviderType.BING,
          ProviderType.TIKTOK,
          ProviderType.APPLOVIN,
          ProviderType.TWITTER,
          // ProviderType.SNAPCHAT,
        ],
        {
          lensGoodsItem: { product },
          // 直接加购的，默认就是Frame Only
          requestData: { currency: currency.name, option: { prescription_type: 'Frame Only' } },
          // 配镜流程那边的架构，totalPrice是传了所有的价格（框+镜片+镀膜）的数据
          // 详情页的不需要，直接取当前商品的售卖价的对应币种的价格
          totalPrice: { sell_price: finalPrice },
          ttqEventId,
        },
        'addToCart.success',
      );

      // Facebook
      // TODO:原有的代码有问题吧？一个track？
      // trackEvent(
      //   {
      //     eventName: 'frame_only',
      //     properties: {},
      //   },
      //   [ProviderType.FACEBOOK],
      // );
    } catch (e) {
      logger.info(e);
    }
  }, [product, currency, userInfo, ttqEventId, trackEvent]);
  /**
   * 初始化埋点
   */
  const initPointData = useCallback(() => {
    try {
      const finalPrice = getPrice(product, currency.name).sell_price;
      const baseFinalPrice = getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price;

      trackEvent(
        undefined,
        [
          ProviderType.FACEBOOK,
          ProviderType.GA4,
          ProviderType.APPLOVIN,
          ProviderType.TIKTOK,
          ProviderType.CRITEO,
          ProviderType.BING,
          ProviderType.SNAPCHAT,
          ProviderType.SNAPCHAT_CONVERSION,
          ProviderType.RTB_HOUSE,
        ],
        {
          product,
          finalPrice,
          baseFinalPrice,
          userInfo,
          ttqEventId,
          currencyName: currency.name,
          searchParams: {
            page: searchParams?.get('page'),
            search_index: searchParams?.get('search_index'),
          },
        },
        'goodDetailView',
      );
    } catch (e) {
      logger.info(e);
    }
  }, [product, currency, userInfo, ttqEventId, trackEvent, searchParams]);

  const getComplementMktBaseParams = (item: any, index: number) => {
    return {
      product_view_uuid: product_view_uuid.current,
      product_index: index,
      shop_sku: item.shop_sku,
      business_sku: item.business_sku,
      product_sku: item.sku,
      product_id: `${item.id}`,
      product_name: item.short_name,
      product_price: parseFloat(getPrice(item, currency.name).price),
      current_product_price: parseFloat(getPrice(item, currency.name).sell_price),
      base_current_product_price: parseFloat(
        getPrice(item, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
      ),
      main_shop_sku: product.shop_sku,
      main_business_sku: product.business_sku,
      main_product_sku: product.sku,
      main_product_id: `${product.id}`,
      main_product_name: product.short_name,
      main_product_price: parseFloat(getPrice(product, currency.name).price),
      main_product_current_price: parseFloat(getPrice(product, currency.name).sell_price),
      main_product_base_product_current_price: parseFloat(
        getPrice(product, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
      ),
      product_class_name: product.products_category,
      is_product_lens: product.is_glass === 1 && product.product_lens === 1,
      currency: currency.name,
    };
  };

  const sdProductDetailsPageComplementMktExp = (item: any, index: number) => {
    try {
      const sdEventName = 'ProductDetailsPageComplementMkt_Exp';
      const sdEventParams = {
        ...getComplementMktBaseParams(item, index),
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };
  const sdProductDetailsPageComplementMktValidExp = (item: any, index: number) => {
    try {
      const sdEventName = `ProductDetailsPageComplementValidMkt_Exp`;
      const sdEventParams = {
        ...getComplementMktBaseParams(item, index),
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };

  const sdProductDetailsComplementMktClick = (item: any, index: number) => {
    try {
      const sdEventName = 'ProductDetailComplementMkt_Click';
      const sdEventParams = {
        ...getComplementMktBaseParams(item, index),
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.info(e);
    }
  };

  useEffect(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    const groupInfo = getGroupInfo();

    // 初始化进入页面场景值并立即清除session，避免影响其他页面
    const goodsDetailEnterPageScene = getSession('zl_goodsDetailEnterPageScene') as string;
    // 判断是否为直接进入（通过URL直接访问或刷新页面）
    const isDirectEnter =
      !refer_url_path || !refer_page_name || refer_url_path === '' || refer_page_name === '';
    // 判断前项页面是否为首页
    const isFromHomePage = refer_url_path === '/' || refer_page_name === '首页';

    if (goodsDetailEnterPageScene) {
      enterPageSceneInternal.current = goodsDetailEnterPageScene;
      removeSession('zl_goodsDetailEnterPageScene');
    } else if (isDirectEnter) {
      enterPageSceneInternal.current = '直接进入';
    } else if (isFromHomePage) {
      enterPageSceneInternal.current = '首页坑位';
    } else {
      enterPageSceneInternal.current = '其他';
    }

    enterTimer.current = Date.now();
    SDProductDetailsPageView();
    initPointData();
    const prevBlogFlags = getBlockFlagsPrev();

    // 监听页面刷新/关闭事件
    const handleBeforeUnload = () => {
      removeGroupInfo();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      SDProductDetailsPageLeave({ refer_page_name, refer_url_path }, prevBlogFlags, groupInfo);
      removeGroupInfo();
    };
  }, []);

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    SD_TryONBtn_Exp,
    SDAddToCartBtnClick,
    SDAddToCartResult,
    SDProductDetailsPageView,
    SDProductDetailsPageLeave,
    SDProductDetailsPageBtnClick,
    SDProductDetailsPageInteractionBtnClick,
    SDProductDetailsPageRelatedMktExp,
    SDProductDetailsPageRelatedMktClick,
    SDProductDetailsPageProductPicExp,
    SDProductDetailsPageYouAlsoLikeMktExp,
    SDProductDetailsPageYouAlsoLikeMktValidExp,
    SDProductDetailsPageYouAlsoLikeMktClick,
    SDProductDetailsPageSeimilarMktClick,
    sendThirdPartyAddToCart,
    sdProductDetailsPageComplementMktExp,
    sdProductDetailsPageComplementMktValidExp,
    sdProductDetailsComplementMktClick,
  };
};
